import * as React from "react"

type Span = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl"
type ResponsiveSpan = Partial<Record<Breakpoint, Span>> & { base?: Span }

export type LayoutCol = {
    field?: string
    span?: Span | ResponsiveSpan
    width?: Span | ResponsiveSpan
    fill?: number | "auto"
    stack?: LayoutRow
}

export type LayoutRow = {
    columns?: LayoutCol[]
    cols?: LayoutCol[]
}

export type Layout = {
    rows: LayoutRow[]
}

type Props = {
    layout: Layout
    renderCol: (
        fieldKey: string,
        col: LayoutCol,
        rowIndex: number,
        colIndex: number,
        isNested?: boolean
    ) => React.ReactNode
    gap?: 1 | 2 | 3 | 4 | 5 | 6
}

const GAP: Record<1 | 2 | 3 | 4 | 5 | 6, string> = {
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
}

const NUMERIC_SPAN_APPLIES_FROM: Breakpoint = "md"

const BASE: Record<Span, string> = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
    5: "col-span-5",
    6: "col-span-6",
    7: "col-span-7",
    8: "col-span-8",
    9: "col-span-9",
    10: "col-span-10",
    11: "col-span-11",
    12: "col-span-12",
}

const SM: Record<Span, string> = {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    9: "sm:col-span-9",
    10: "sm:col-span-10",
    11: "sm:col-span-11",
    12: "sm:col-span-12",
}

const MD: Record<Span, string> = {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    7: "md:col-span-7",
    8: "md:col-span-8",
    9: "md:col-span-9",
    10: "md:col-span-10",
    11: "md:col-span-11",
    12: "md:col-span-12",
}

const LG: Record<Span, string> = {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
    5: "lg:col-span-5",
    6: "lg:col-span-6",
    7: "lg:col-span-7",
    8: "lg:col-span-8",
    9: "lg:col-span-9",
    10: "lg:col-span-10",
    11: "lg:col-span-11",
    12: "lg:col-span-12",
}

const XL: Record<Span, string> = {
    1: "xl:col-span-1",
    2: "xl:col-span-2",
    3: "xl:col-span-3",
    4: "xl:col-span-4",
    5: "xl:col-span-5",
    6: "xl:col-span-6",
    7: "xl:col-span-7",
    8: "xl:col-span-8",
    9: "xl:col-span-9",
    10: "xl:col-span-10",
    11: "xl:col-span-11",
    12: "xl:col-span-12",
}

const XXL: Record<Span, string> = {
    1: "2xl:col-span-1",
    2: "2xl:col-span-2",
    3: "2xl:col-span-3",
    4: "2xl:col-span-4",
    5: "2xl:col-span-5",
    6: "2xl:col-span-6",
    7: "2xl:col-span-7",
    8: "2xl:col-span-8",
    9: "2xl:col-span-9",
    10: "2xl:col-span-10",
    11: "2xl:col-span-11",
    12: "2xl:col-span-12",
}

function getRowColumns(row: LayoutRow | null | undefined): LayoutCol[] {
    if (!row) return []
    if (Array.isArray(row.columns)) return row.columns
    if (Array.isArray(row.cols)) return row.cols
    return []
}

function getField(col: LayoutCol): string | null {
    if (typeof col.field === "string" && col.field.trim()) return col.field
    return null
}

type IndexPath = number[]

function makeColIndex(path: IndexPath): number {
    let n = 0
    for (const p of path) n = n * 100 + p
    return n
}

function normalizeResponsiveSpan(v: Span | ResponsiveSpan | undefined): ResponsiveSpan {
    if (!v) return { base: 12 }

    if (typeof v === "number") {
        return { base: 12, [NUMERIC_SPAN_APPLIES_FROM]: v }
    }

    const base = v.base
    if (typeof base === "number" && base >= 1 && base <= 12) return v

    return { ...v, base: 12 }
}

function getSpanMap(col: LayoutCol): ResponsiveSpan {
    const v = col.width ?? col.span
    return normalizeResponsiveSpan(v)
}

function clsBase(span: Span): string {
    return BASE[span]
}

function clsBp(bp: Breakpoint, span: Span): string {
    if (bp === "sm") return SM[span]
    if (bp === "md") return MD[span]
    if (bp === "lg") return LG[span]
    if (bp === "xl") return XL[span]
    return XXL[span]
}

function spanClasses(m: ResponsiveSpan): string {
    const base = (m.base ?? 12) as Span
    const parts: string[] = [clsBase(base)]

    const order: Breakpoint[] = ["sm", "md", "lg", "xl", "2xl"]
    for (const bp of order) {
        const v = m[bp]
        if (typeof v !== "number") continue
        if (v < 1 || v > 12) continue
        parts.push(clsBp(bp, v as Span))
    }

    return parts.join(" ")
}

type RenderGridProps = {
    row: LayoutRow
    outerRowIndex: number
    path: IndexPath
    gapClass: string
    renderCol: Props["renderCol"]
}

function RenderGrid({ row, outerRowIndex, path, gapClass, renderCol }: RenderGridProps) {
    const cols = getRowColumns(row)

    const autoFills = cols.filter(c => c.fill === "auto")
    if (autoFills.length > 1) {
        throw new Error("Only one fill:'auto' column allowed per row")
    }

    return (
        <div className={`grid grid-cols-12 ${gapClass} w-full min-w-0 items-start`}>
            {cols.map((col, ci) => (
                <RenderColumn
                    key={makeColIndex([...path, ci])}
                    col={col}
                    outerRowIndex={outerRowIndex}
                    path={[...path, ci]}
                    gapClass={gapClass}
                    renderCol={renderCol}
                />
            ))}
        </div>
    )
}

type RenderColumnProps = {
    col: LayoutCol
    outerRowIndex: number
    path: IndexPath
    gapClass: string
    renderCol: Props["renderCol"]
}

function RenderColumn({ col, outerRowIndex, path, gapClass, renderCol }: RenderColumnProps) {
    const spanMap = getSpanMap(col)
    const cls = spanClasses(spanMap)

    if (col.stack) {
        return (
            <div className={`${cls} w-full min-w-0`}>
                <RenderGrid
                    row={col.stack}
                    outerRowIndex={outerRowIndex}
                    path={path}
                    gapClass={gapClass}
                    renderCol={renderCol}
                />
            </div>
        )
    }

    const fieldKey = getField(col)
    if (!fieldKey) return null

    const style =
        typeof col.fill === "number"
            ? { height: `${col.fill}px` }
            : col.fill === "auto"
                ? { height: "100%" }
                : undefined

    return (
        <div className={`${cls} w-full min-w-0`} style={style}>
            {renderCol(fieldKey, col, outerRowIndex, makeColIndex(path))}
        </div>
    )
}

export function Rows({ layout, renderCol, gap = 3 }: Props) {
    const gapClass = GAP[gap]
    const rows = layout?.rows ?? []

    return (
        <div className="flex flex-col gap-6 w-full min-w-0">
            {rows.map((row, ri) => (
                <RenderGrid
                    key={ri}
                    row={row}
                    outerRowIndex={ri}
                    path={[]}
                    gapClass={gapClass}
                    renderCol={renderCol}
                />
            ))}
        </div>
    )
}
